/* What a typed cashier amount may be, by the whitelabel's own rules
   (batman's WalletHub/amountValidation.ts), message for message — so a deposit
   or a withdrawal refused here reads the same as on the platform's own front
   end. Comma and full stop are both decimal separators, as a European keyboard
   types them.

   Every limit is a base-unit bigint at the token's `decimals`. Which token that
   is matters: a withdrawal is taken from the bankroll balance, so it is the
   bankroll token's scale; a deposit arrives in the route's `fromToken`. */

export type AmountRules = {
  minAmount: bigint;
  /* The most that can be sent — a balance — or null when it is not known. */
  maxAmount: bigint | null;
  decimals: number;
  significantDecimals: number;
  actionLabel: "deposit" | "withdrawal";
};

/* Anything but digits and one separator is a character the field drops as it
   is typed, rather than one it complains about. */
export const hasInvalidAmountCharacters = (value: string) =>
  /[^0-9.,]/.test(value) || (value.match(/[.,]/g)?.length ?? 0) > 1;

export const sanitizeAmountInput = (value: string) => value.replace(/[^0-9.,]/g, "");

/* The typed figure in base units, or null when it is not a plain decimal or
   carries more places than the token has. */
export const parseTypedAmount = (value: string, decimals: number): bigint | null => {
  const normalized = value.trim().replace(/,/g, ".");

  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }

  const [whole, fraction = ""] = normalized.split(".");

  if (fraction.length > decimals) {
    return null;
  }

  return BigInt(whole + fraction.padEnd(decimals, "0"));
};

/* The whitelabel's formatUnitsDecimals: rounded to `fractionDigits` in the
   given direction, trailing zeros dropped but never fewer than two places,
   thousands grouped. A minimum is shown rounded up, so typing back exactly the
   figure printed always clears it. */
export const formatUnitsDecimals = ({
  value,
  decimals,
  fractionDigits,
  roundingMode,
}: {
  value: bigint;
  decimals: number;
  fractionDigits: number;
  roundingMode: "up" | "down";
}) => {
  const negative = value < 0n;
  const absolute = negative ? -value : value;

  const kept = Math.min(fractionDigits, decimals);
  const scale = 10n ** BigInt(decimals - kept);
  const rounded = roundingMode === "up" ? (absolute + scale - 1n) / scale : absolute / scale;
  const raw = rounded.toString().padStart(kept + 1, "0");

  const whole = raw.slice(0, raw.length - kept).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = raw.slice(raw.length - kept).replace(/0+$/, "").padEnd(2, "0");

  return `${negative ? "-" : ""}${whole || "0"}.${fraction}`;
};

export const formatMinimum = (minAmount: bigint, token: { decimals: number; significantDecimals: number }) =>
  formatUnitsDecimals({
    value: minAmount,
    decimals: token.decimals,
    fractionDigits: token.significantDecimals,
    roundingMode: "up",
  });

/* null when the amount is acceptable — or still empty: whether an empty field
   is worth a message is the form's call, not this rule's. */
export const amountError = (
  value: string,
  { minAmount, maxAmount, decimals, significantDecimals, actionLabel }: AmountRules,
): string | null => {
  const normalized = value.trim().replace(/,/g, ".");

  if (normalized.length === 0) {
    return null;
  }

  if (/[.,]$/.test(normalized) || !/^\d+(\.\d+)?$/.test(normalized)) {
    return "Enter a valid amount";
  }

  const places = Math.min(decimals, significantDecimals);
  const fraction = normalized.split(".").at(1);

  if (fraction !== undefined && fraction.length > places) {
    return `Maximum ${places} decimal places`;
  }

  const parsed = parseTypedAmount(normalized, decimals);

  if (parsed === null) {
    return "Enter a valid amount";
  }

  if (parsed <= 0n) {
    return "Amount must be greater than zero";
  }

  if (parsed < minAmount) {
    return `Minimum ${actionLabel} is ${formatUnitsDecimals({
      value: minAmount,
      decimals,
      fractionDigits: significantDecimals,
      roundingMode: "up",
    })}`;
  }

  if (maxAmount !== null && parsed > maxAmount) {
    return "Not enough balance";
  }

  return null;
};
